import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"
import { ChevronRight } from "@styled-icons/feather/ChevronRight"
import classNames from "classnames"
import BreadCrumb from "customerAttachments/components/BreadCrumb"
import Card from "customerAttachments/components/Card"
import Loading from "customerAttachments/components/Loading"
import { OrderTypeMapping } from "customerAttachments/constants"
import useWindowDimensions, { DeviceType } from "customerAttachments/hooks/useWIndowDimensions"
import { OrderType } from "customerAttachments/types"
import { useNavigation } from "modules/navigation"

import styles from "./styles.module.scss"

type Data = {
  Icon: React.ComponentType
  Text: string
  IsDisabled: boolean
  Status: string
  ErrorText?: string
  AttachType: string
}

type AttachmentListProps = {
  data: Data[]
  orderType: OrderType
  isLoading: boolean
}

const AttachmentList = ({ data, orderType, isLoading }: AttachmentListProps) => {
  const { navigateTo } = useNavigation()
  const { transferId = "", orderNo = "" } = useParams<{ transferId: string; orderNo: string }>()
  const orderTypeText = OrderTypeMapping[orderType]
  const { t } = useTranslation(["sales"])
  const { height, deviceType } = useWindowDimensions()
  const isMobile = deviceType === DeviceType.MOBILE

  const handleItemClick = (attachType: string) => {
    navigateTo(`/${orderType}/${transferId || orderNo}/attachment?type=${attachType}`)
  }

  return (
    <>
      <BreadCrumb text={`${orderTypeText}：${transferId || orderNo}`} onClick={() => navigateTo(`/`)} />
      <div className={styles.listLayout_container} style={{ height: `calc(${height}px - ${isMobile ? 119 : 143}px)` }}>
        <Loading loading={isLoading}>
          <Card
            title={t("customer_attachments.upload_files_list")}
            content={
              <>
                {data?.map((item) => {
                  const {
                    Icon,
                    Text: attachName,
                    IsDisabled: isDisabled,
                    Status: statusText,
                    ErrorText: errorText,
                    AttachType,
                  } = item
                  return (
                    <div
                      data-testid={AttachType}
                      aria-disabled={isDisabled ? "true" : "false"}
                      className={classNames(styles.listLayout_attachItem_container, {
                        [styles.disabled]: isDisabled,
                      })}
                      key={AttachType}>
                      <div
                        className={classNames(styles.listLayout_attachItem, {
                          [styles.disabled]: isDisabled,
                        })}
                        onClick={() => {
                          if (!isDisabled) {
                            handleItemClick(AttachType)
                          }
                        }}>
                        <div className={styles.listLayout_attachItem_content}>
                          {Icon && (
                            <div className={styles.listLayout_attachItem_icon}>
                              <Icon data-testid={`${AttachType}-icon`} />
                            </div>
                          )}
                          <div>{attachName}</div>
                        </div>
                        {!isDisabled && <ChevronRight size={24} />}
                      </div>
                      <p
                        data-testid={`${AttachType}-note`}
                        className={classNames(styles.listLayout_attachItem_note, {
                          [styles.error]: !!errorText,
                        })}>
                        {errorText || statusText}
                      </p>
                    </div>
                  )
                })}
              </>
            }
          />
        </Loading>
      </div>
    </>
  )
}

export default AttachmentList
